/** Attachment encryption helpers — SPEC §14 / SEC-MED-* */
import { z } from "zod";

export const CONTENT_TYPE_ATTACHMENT = 9;

/** Largest seal plaintext (SPEC §9.5 bucket 8192 minus 2-byte length prefix). */
export const MAX_SEAL_PLAINTEXT = 8190;

export interface AttachmentChunkRef {
  store: "a" | "b";
  objectId: string;
  size: number;
  ciphertextHash: string;
}

export interface AttachmentManifest {
  v: 1;
  manifestId: string;
  mediaType: string;
  fileName: string;
  totalSize: number;
  chunks: AttachmentChunkRef[];
  /** Wrapped file key travels only inside conversation E2EE envelope (SEC-MED-002). */
  fileKeyB64: string;
  fileNonceB64: string;
}

export const ATTACHMENT_FEATURE_STATUS = {
  chunkEncrypt: "partial",
  dualObjectStore: "infra-ready",
  availableInUi: true,
  gateMessage: "",
} as const;

export const CHUNK_SIZE = 256 * 1024;

function b64(bytes: Uint8Array): string {
  let s = "";
  for (const b of bytes) s += String.fromCharCode(b);
  return btoa(s);
}

function unb64(s: string): Uint8Array {
  const bin = atob(s);
  const out = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) out[i] = bin.charCodeAt(i);
  return out;
}

function asBuf(bytes: Uint8Array): ArrayBuffer {
  return bytes.buffer.slice(bytes.byteOffset, bytes.byteOffset + bytes.byteLength) as ArrayBuffer;
}

async function sha256Hex(bytes: Uint8Array): Promise<string> {
  const digest = await crypto.subtle.digest("SHA-256", asBuf(bytes));
  return [...new Uint8Array(digest)].map((b) => b.toString(16).padStart(2, "0")).join("");
}

/** Encrypt file locally; returns ciphertext blobs + manifest (no plaintext filenames in object ids). */
export async function encryptAttachmentFile(
  file: Blob,
  fileName: string,
  mediaType: string,
): Promise<{ manifest: AttachmentManifest; chunks: Uint8Array[] }> {
  const fileKey = crypto.getRandomValues(new Uint8Array(32));
  const fileNonce = crypto.getRandomValues(new Uint8Array(12));
  const key = await crypto.subtle.importKey("raw", asBuf(fileKey), "AES-GCM", false, [
    "encrypt",
    "decrypt",
  ]);
  const plain = new Uint8Array(await file.arrayBuffer());
  const chunks: Uint8Array[] = [];
  const refs: AttachmentChunkRef[] = [];
  const manifestId = b64(crypto.getRandomValues(new Uint8Array(16))).replace(/[^a-zA-Z0-9]/g, "");

  for (let offset = 0, i = 0; offset < plain.length; offset += CHUNK_SIZE, i++) {
    const slice = plain.subarray(offset, Math.min(offset + CHUNK_SIZE, plain.length));
    const iv = crypto.getRandomValues(new Uint8Array(12));
    // Bind chunk index in AAD
    const aad = new TextEncoder().encode(`veto/v1/chunk/${i}/${plain.length}`);
    const ct = new Uint8Array(
      await crypto.subtle.encrypt({ name: "AES-GCM", iv, additionalData: aad }, key, asBuf(slice)),
    );
    const packed = new Uint8Array(iv.length + ct.length);
    packed.set(iv, 0);
    packed.set(ct, iv.length);
    chunks.push(packed);
    const objectId = await sha256Hex(packed);
    refs.push({
      store: i % 2 === 0 ? "a" : "b",
      objectId,
      size: packed.length,
      ciphertextHash: objectId,
    });
  }

  return {
    manifest: {
      v: 1,
      manifestId,
      mediaType,
      fileName,
      totalSize: plain.length,
      chunks: refs,
      fileKeyB64: b64(fileKey),
      fileNonceB64: b64(fileNonce),
    },
    chunks,
  };
}

export async function decryptAttachmentChunk(
  packed: Uint8Array,
  fileKeyB64: string,
  chunkIndex: number,
  totalSize: number,
): Promise<Uint8Array> {
  const fileKey = unb64(fileKeyB64);
  const key = await crypto.subtle.importKey("raw", asBuf(fileKey), "AES-GCM", false, ["decrypt"]);
  const iv = packed.subarray(0, 12);
  const ct = packed.subarray(12);
  const aad = new TextEncoder().encode(`veto/v1/chunk/${chunkIndex}/${totalSize}`);
  const pt = await crypto.subtle.decrypt(
    { name: "AES-GCM", iv: asBuf(iv), additionalData: aad },
    key,
    asBuf(ct),
  );
  return new Uint8Array(pt);
}

/** Lab MinIO / object-store-dev (SEC-MED-002). */
export const DEFAULT_ATTACHMENT_ENDPOINTS = {
  a: "http://127.0.0.1:9000/veto-a",
  b: "http://127.0.0.1:9002/veto-b",
} as const;

function readViteEnv(key: string): string | undefined {
  try {
    const env = (import.meta as ImportMeta & { env?: Record<string, unknown> }).env;
    const v = env?.[key];
    return typeof v === "string" && v.trim().length > 0 ? v.trim() : undefined;
  } catch {
    return undefined;
  }
}

function isProdClientBuild(): boolean {
  try {
    const env = (import.meta as ImportMeta & { env?: Record<string, unknown> }).env;
    return env?.PROD === true || env?.MODE === "production";
  } catch {
    return false;
  }
}

/**
 * Resolve dual ciphertext store bases (SEC-MED-002).
 * - `VITE_ATTACHMENT_STORE_A/B` when set
 * - Production: same-origin `/api/ciphertext/{a|b}` (Vercel Blob proxy)
 * - Dev/lab: local MinIO / object-store-dev
 */
export function resolveAttachmentEndpoints(override?: {
  a: string;
  b: string;
}): { a: string; b: string } {
  if (override?.a && override?.b) return override;
  const a = readViteEnv("VITE_ATTACHMENT_STORE_A");
  const b = readViteEnv("VITE_ATTACHMENT_STORE_B");
  if (a && b) return { a, b };
  if (isProdClientBuild()) {
    return { a: "/api/ciphertext/a", b: "/api/ciphertext/b" };
  }
  return { a: DEFAULT_ATTACHMENT_ENDPOINTS.a, b: DEFAULT_ATTACHMENT_ENDPOINTS.b };
}

/** Dual-store upload to MinIO-compatible or same-origin API endpoints (H5 / SEC-MED-002). */
export async function uploadChunksDual(
  chunks: Uint8Array[],
  refs: AttachmentChunkRef[],
  endpoints: { a: string; b: string } = resolveAttachmentEndpoints(),
): Promise<void> {
  await Promise.all(
    chunks.map(async (chunk, i) => {
      const ref = refs[i]!;
      const base = ref.store === "a" ? endpoints.a : endpoints.b;
      const url = `${base}/${ref.objectId}`;
      const res = await fetch(url, {
        method: "PUT",
        body: asBuf(chunk),
        headers: { "content-type": "application/octet-stream" },
        signal: AbortSignal.timeout(8000),
      });
      if (!res.ok && res.status !== 200 && res.status !== 201 && res.status !== 204) {
        throw new Error(`upload failed ${res.status} ${url}`);
      }
    }),
  );
}

/** Object ids are SHA-256 content addresses of the packed ciphertext chunk. */
const OBJECT_ID_RE = /^[0-9a-f]{64}$/;
/** Packed chunk = iv(12) ‖ AES-GCM(≤ CHUNK_SIZE) ‖ tag(16). */
const MAX_PACKED_CHUNK = CHUNK_SIZE + 12 + 16;

/**
 * Fetch ciphertext chunks from dual object stores (SEC-MED-002 receive path).
 * Each chunk must hash to its content address (objectId) before decryption, so
 * a store (or anyone who overwrote the object) cannot swap ciphertexts, and the
 * sender-chosen id can never steer the request outside the store namespace.
 */
export async function downloadChunksDual(
  refs: AttachmentChunkRef[],
  endpoints: { a: string; b: string } = resolveAttachmentEndpoints(),
): Promise<Uint8Array[]> {
  if (refs.length === 0 || refs.length > 64) throw new Error("attachment chunk count out of range");
  return Promise.all(
    refs.map(async (ref) => {
      if (!OBJECT_ID_RE.test(ref.objectId) || (ref.store !== "a" && ref.store !== "b")) {
        throw new Error("invalid attachment chunk reference");
      }
      const base = ref.store === "a" ? endpoints.a : endpoints.b;
      const url = `${base}/${ref.objectId}`;
      const res = await fetch(url, {
        method: "GET",
        signal: AbortSignal.timeout(15000),
      });
      if (!res.ok) {
        throw new Error(`download failed ${res.status}`);
      }
      const packed = new Uint8Array(await res.arrayBuffer());
      if (packed.length > MAX_PACKED_CHUNK || (await sha256Hex(packed)) !== ref.objectId) {
        throw new Error("attachment chunk failed content-address check");
      }
      return packed;
    }),
  );
}

export type AttachmentWireKind = "image" | "document" | "view-once" | "voice" | "sticker" | "gif";

export type AttachmentDelivery = "inline" | "object-store";

/**
 * Build sealed attachment JSON that fits the ratchet pad budget (SEC-MED-001/002, §9.5).
 * Prefers dual object-store upload; inlines ciphertext when the full body fits in 8190 bytes.
 */
export async function prepareAttachmentPayload(input: {
  file: Blob;
  fileName: string;
  mediaType: string;
  kind: AttachmentWireKind;
  durationMs?: number;
  endpoints?: { a: string; b: string };
}): Promise<{
  manifest: AttachmentManifest;
  sealedBody: string;
  label: string;
  delivery: AttachmentDelivery;
}> {
  const { manifest, chunks } = await encryptAttachmentFile(
    input.file,
    input.fileName,
    input.mediaType,
  );

  const endpoints = resolveAttachmentEndpoints(input.endpoints);
  let uploaded = false;
  try {
    await uploadChunksDual(chunks, manifest.chunks, endpoints);
    uploaded = true;
  } catch {
    /* best-effort — SEC-MED-002 dual store may be offline in lab */
  }

  const seconds = Math.max(1, Math.round((input.durationMs ?? 1000) / 1000));
  const label =
    input.kind === "voice"
      ? `🎤 Voice note (${seconds}s)`
      : input.kind === "view-once"
        ? `👁 View once · ${input.fileName}`
        : input.kind === "sticker"
          ? "🏷️ Sticker"
          : input.kind === "gif"
            ? `🎞️ ${input.fileName}`
            : `📎 ${input.fileName}`;

  const base: Record<string, unknown> = {
    op: "attachment",
    kind: input.kind,
    manifest,
  };
  if (input.kind === "voice" && input.durationMs != null) {
    base.durationMs = input.durationMs;
  }

  const utf8Len = (s: string) => new TextEncoder().encode(s).length;
  const withInline = JSON.stringify({
    ...base,
    inlineChunksB64: chunks.map((c) => b64(c)),
  });
  if (utf8Len(withInline) <= MAX_SEAL_PLAINTEXT) {
    return {
      manifest,
      sealedBody: withInline,
      label,
      delivery: "inline",
    };
  }

  const manifestOnly = JSON.stringify(base);
  if (utf8Len(manifestOnly) > MAX_SEAL_PLAINTEXT) {
    throw new Error("Attachment manifest exceeds seal padding budget (SPEC §9.5)");
  }
  if (!uploaded) {
    throw new Error(
      "Attachment too large for inline E2EE delivery and object stores unavailable",
    );
  }
  return {
    manifest,
    sealedBody: manifestOnly,
    label,
    delivery: "object-store",
  };
}


// ---------------------------------------------------------------------------
// Inbound validation (never trust a peer-supplied manifest shape)
// ---------------------------------------------------------------------------

const ChunkRefSchema = z
  .object({
    store: z.enum(["a", "b"]),
    objectId: z.string().regex(OBJECT_ID_RE),
    size: z.number().int().min(0).max(MAX_PACKED_CHUNK),
    ciphertextHash: z.string().regex(OBJECT_ID_RE),
  })
  .strict();

const ManifestSchema = z
  .object({
    v: z.literal(1).optional(),
    manifestId: z.string().max(64).optional(),
    mediaType: z.string().max(128),
    fileName: z.string().max(256),
    totalSize: z.number().int().min(0).max(64 * 1024 * 1024),
    chunks: z.array(ChunkRefSchema).max(64).optional(),
    fileKeyB64: z.string().regex(/^[A-Za-z0-9+/]{43}=$/),
    fileNonceB64: z.string().max(32).optional(),
  })
  .passthrough();

export const InboundAttachmentSchema = z
  .object({
    op: z.string().max(32).optional(),
    kind: z.string().max(32).optional(),
    pack: z.string().max(64).optional(),
    id: z.string().max(64).optional(),
    durationMs: z.number().int().min(0).max(60 * 60 * 1000).optional(),
    /** Disappearing timer of the chat when it was sent (phone parity, review M3). */
    disappearSeconds: z.number().int().min(0).max(90 * 86400).optional(),
    manifest: ManifestSchema.optional(),
    inlineChunksB64: z.array(z.string().max(MAX_PACKED_CHUNK * 2)).max(64).optional(),
  })
  .passthrough();

export type InboundAttachment = z.infer<typeof InboundAttachmentSchema>;

/** Parse an inbound attachment body; null when malformed. */
export function parseInboundAttachment(body: string): InboundAttachment | null {
  try {
    const parsed = InboundAttachmentSchema.safeParse(JSON.parse(body));
    return parsed.success ? parsed.data : null;
  } catch {
    return null;
  }
}
